/**
 * `useAdvances` — live snapshot hooks for staff advances.
 *
 * Three hooks, all backed by indexed Firestore queries on the
 * shared `transactions` collection:
 *
 *   - useAdvanceChildren   — the children of one parent advance
 *                            (for the expand-row UI on the
 *                            Transactions tab + the close-advance
 *                            preview).
 *   - useOpenAdvances      — every open / partial advance on a
 *                            project (for grouping in the project
 *                            Transactions tab).
 *   - usePartyOpenAdvances — every open / partial advance held by
 *                            one party (for the "Open advances"
 *                            card on the Party Detail page).
 *
 * All three mirror the loading / error / data shape of the
 * existing `useProjectTransactions` hook so consumers swap in
 * cleanly.
 *
 * Schema parity with mobile: same field names, same query
 * shapes — the indexes in `firestore.indexes.json` cover both
 * clients.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type { TransactionItem } from '@/lib/useProjectTransactions';

function txDateMs(t: TransactionItem): number {
  return (
    t.date?.toMillis?.() ??
    t.transactionDate?.toMillis?.() ??
    t.createdAt?.toMillis?.() ??
    0
  );
}

// ───── useAdvanceChildren ────────────────────────────────────

export function useAdvanceChildren(
  orgId: string | null,
  advanceId: string | null,
): {
  children: TransactionItem[];
  loading: boolean;
  error: string | null;
} {
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !advanceId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // Backed by index: (orgId, parentTransactionId, date DESC).
    // The same query runs from web + mobile.
    return onSnapshot(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('parentTransactionId', '==', advanceId),
      ),
      (snap) => {
        setItems(
          snap.docs.map((d) => ({
            ...(d.data() as TransactionItem),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useAdvanceChildren] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, advanceId]);

  const sorted = useMemo(
    () => [...items].sort((a, b) => txDateMs(a) - txDateMs(b)),
    [items],
  );

  return { children: sorted, loading, error };
}

// ───── useOpenAdvances ──────────────────────────────────────

/** All non-settled advances on a project. Settled advances drop
 *  out of this list — they still show in the main transactions
 *  table, just not in the "Open advances" summary band. */
export function useOpenAdvances(
  orgId: string | null,
  projectId: string | null,
): {
  advances: TransactionItem[];
  loading: boolean;
  error: string | null;
} {
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !projectId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // We fetch by (orgId, projectId, category=staff_advance) and
    // filter advanceStatus client-side. Using a composite index
    // with advanceStatus would force us to query each status
    // separately (Firestore can't filter "!= settled" cheaply on
    // a non-key field). Client-side filter is fine because each
    // project has at most a handful of advances.
    return onSnapshot(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
        where('category', '==', 'staff_advance'),
      ),
      (snap) => {
        setItems(
          snap.docs.map((d) => ({
            ...(d.data() as TransactionItem),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useOpenAdvances] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, projectId]);

  const open = useMemo(
    () =>
      items
        .filter((t) => t.advanceStatus !== 'settled')
        .sort((a, b) => txDateMs(b) - txDateMs(a)),
    [items],
  );

  return { advances: open, loading, error };
}

// ───── usePartyOpenAdvances ─────────────────────────────────

/** Every open / partial advance held by a party (across all
 *  projects). Drives the "OPEN ADVANCES" card on the Party
 *  Detail page so a studio admin can see at a glance who's
 *  holding company cash. */
export function usePartyOpenAdvances(
  orgId: string | null,
  partyId: string | null,
): {
  advances: TransactionItem[];
  loading: boolean;
  error: string | null;
} {
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !partyId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collection(db, 'transactions'),
        where('orgId', '==', orgId),
        where('partyId', '==', partyId),
        where('category', '==', 'staff_advance'),
      ),
      (snap) => {
        setItems(
          snap.docs.map((d) => ({
            ...(d.data() as TransactionItem),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[usePartyOpenAdvances] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, partyId]);

  const open = useMemo(
    () =>
      items
        .filter((t) => t.advanceStatus !== 'settled')
        .sort((a, b) => txDateMs(b) - txDateMs(a)),
    [items],
  );

  return { advances: open, loading, error };
}
